import { Button } from "@/src/components/atom/Button";
import Dialog from "@/src/components/atom/Dialog";
import DialogFooterContainer from "@/src/components/atom/Dialog/DialogFooterContainer";
import Typo from "@/src/components/atom/Typo";
import CancelAlertDialog from "@/src/components/molecule/CancelAlertDialog";
import ContactFormItem from "@/src/components/molecule/ContactFormItem";
import FormItem from "@/src/components/molecule/FormItem";
import { CLIENT_TYPE_OPTION_LIST } from "@/src/constant/optionList";
import useAlert from "@/src/hooks/useAlert";
import { authApi } from "@/src/store/apis/auth";
import { ClientType, ExporterUserType } from "@/src/store/apis/auth/interface";
import { useGetCommonCodeViaCodeNameQuery } from "@/src/store/apis/common";
import { PartialCommonCodeItemDto } from "@/src/store/apis/common/interface";
import { useEditStaffMutation } from "@/src/store/apis/corporate/staffManagement";
import { ExporterUserListViewDto } from "@/src/store/apis/corporate/staffManagement/interface";
import { useLazyGetCurrentSubscriptionQuery } from "@/src/store/apis/subscription";
import colorSet from "@/src/styles/color";
import { changeEmailCharacters } from "@/src/utils/format";
import { isUndefined } from "@/src/utils/is";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import EditAlertDialog from "./EditAlertDialog";
import { useAppSelector } from "@/src/store";
import { getFormattedTimezoneDateWithOffset } from "@/src/hooks/useCompanyLocalDate";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";

interface EditDialogProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  selectData: ExporterUserListViewDto | null;
}

const mainCategoryEmptyArray: PartialCommonCodeItemDto[] = [];

function EditDialog({ open, onOpenChange, selectData }: EditDialogProps) {
  const { t } = useTranslation();
  const alert = useAlert();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const subscription = useAppSelector((state) => state.subscription);

  const isCompanySubscription = subscription.isCompanySubscription;

  // 기업이 Both 타입인지
  const isBothType =
    selectData?.exporterUserListQueryResultDto.exporter.companyType === "BOTH";

  // API
  const [getCurrentSubscription] = useLazyGetCurrentSubscriptionQuery();
  const {
    mainCategoryCodeItemNames,
    exporterTelPrefix,
    exporterOfficeContactPrefix,
    isCorporateManager,
    exporterUserType,
    userMainCategory,
    gmtOffset,
  } = authApi.endpoints.getSession.useQueryState(undefined, {
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isError || isFetching || isUndefined(currentData);
      const isStable = !isUnstable;
      return {
        isCorporateManager: isStable
          ? currentData.row.exporterUserType === "CORPORATE_MANAGER"
          : false,
        mainCategoryCodeItemNames: isStable
          ? currentData?.row.exporter.mainCategoryCodeItemNames
          : [],
        exporterTelPrefix: isStable
          ? (currentData?.row.exporter.telPrefix ?? " ")
          : " ",
        exporterOfficeContactPrefix: isStable
          ? (currentData?.row.exporter.faxPrefix ?? " ")
          : " ",
        exporterUserType: currentData?.row.exporterUserType,
        userMainCategory: isStable
          ? currentData.row.mainCategoryCodeItemNames
          : [],
        gmtOffset: currentData?.row.exporter.gmtOffset,
      };
    },
  });

  const [editStaff] = useEditStaffMutation();
  const { mainCategoryCurrentData } = useGetCommonCodeViaCodeNameQuery(
    {
      codeName: "MAIN_CATEGORY",
    },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isError || isFetching || isUndefined(currentData);

        return {
          mainCategoryCurrentData: !isUnstable
            ? (currentData ?? mainCategoryEmptyArray)
            : mainCategoryEmptyArray,
        };
      },
    }
  );

  const mainCategoryStringList = useMemo(
    () => mainCategoryCurrentData.map((item) => item.codeItemName),
    [mainCategoryCurrentData]
  );

  const mainCategoryFilterList = mainCategoryCurrentData
    .filter((item) => mainCategoryCodeItemNames.includes(item.codeItemName))
    .map((item) => {
      return {
        label: item.codeItemNameEn,
        value: item.codeItemName,
      };
    });

  // State
  const [isAdminAlertDialogOpen, setIsAdminAlertDialogOpen] = useState(false);
  const [isAdminSeconds, setIsAdminSeconds] = useState(false);
  const [isCancelAlertOpen, setIsCancelAlertOpen] = useState(false);

  const {
    control,
    watch,
    handleSubmit,
    register,
    setValue,
    setFocus,
    getValues,
  } = useForm({
    mode: "all",
    defaultValues: {
      name: selectData?.name as string,
      email: changeEmailCharacters(selectData?.email as string),
      exporterUserType: selectData?.exporterUserType as ExporterUserType,
      isActivated: selectData?.isActivated === true ? "Y" : "N",
      mainCategory: selectData?.mainCategory
        ? selectData.exporterUserListQueryResultDto.mainCategoryCodeItemNames
        : [],
      personalContactPrefix: selectData?.personalContactPrefix
        ? selectData?.personalContactPrefix
        : exporterTelPrefix,
      personalContact: selectData?.personalContact ?? "",
      officeContactPrefix: selectData?.officeContactPrefix
        ? selectData?.officeContactPrefix
        : exporterOfficeContactPrefix,
      officeContact: selectData?.officeContact ?? "",
      exporterUserMainFieldType:
        selectData?.exporterUserMainFieldType as ClientType,
    },
  });

  const handleSubmitClick = async () => {
    if (!selectData) {
      return;
    }

    const params = {
      id: selectData.exporterUserListQueryResultDto.id,
      name: getValues("name"),
      email: selectData?.email,
      exporterUserType: getValues("exporterUserType"),
      mainCategoryCodeItemNames: getValues("mainCategory"),
      personalContactPrefix:
        getValues("personalContactPrefix") !== " "
          ? getValues("personalContactPrefix")
          : null,
      personalContact: getValues("personalContact") || null,
      officeContactPrefix:
        getValues("officeContactPrefix") !== " "
          ? getValues("officeContactPrefix")
          : null,
      officeContact: getValues("officeContact") || null,
      isActivated: getValues("isActivated") === "Y",
      exporterUserMainFieldType: getValues("exporterUserMainFieldType"),
    };

    try {
      await editStaff(params).unwrap();
      await getCurrentSubscription();

      alert.showAlert({
        type: "success",
        message: t("alert:saveSuccess"),
      });
      onOpenChange(false);
    } catch (e: any) {
      // 기업관리자가 정해진 최대 인원수(2)를 초과하였을 경우
      if (
        e.data.errorCode ===
        "EXPORTER_USER_CORPORATE_MANAGER_MAX_COUNT_EXCEEDED"
      ) {
        setIsAdminAlertDialogOpen(true);
        setIsAdminSeconds(true);
      }

      // 기업관리자가 정해진 미만
      if (
        e.data.errorCode ===
        "EXPORTER_USER_CORPORATE_MANAGER_LESS_THAN_MIN_COUNT"
      ) {
        setIsAdminAlertDialogOpen(true);
        setIsAdminSeconds(false);
      }

      if (
        e.data.errorCode !==
          "EXPORTER_USER_CORPORATE_MANAGER_LESS_THAN_MIN_COUNT" &&
        e.data.errorCode !==
          "EXPORTER_USER_CORPORATE_MANAGER_MAX_COUNT_EXCEEDED"
      ) {
        const message = Array.isArray(e.data.message)
          ? e.data.message[0]
          : e.data.message;
        alert.showAlert({ type: "error", message });
      }
    }
  };

  useEffect(() => {
    setFocus("name");
  }, [setFocus]);

  return (
    <Dialog
      title={t("staffManagement:exporter.editDialog.title")}
      open={open}
      onOpenChange={onOpenChange}
      destroyDialogWhenEscapePress={false}
      onEscapeKeyDown={() => setIsCancelAlertOpen(true)}
      footer={
        <DialogFooterContainer>
          <Button
            buttonGrade="tertiary"
            buttonColor="black"
            onClick={() => setIsCancelAlertOpen(true)}
            ref={cancelButtonRef}
            onKeyUp={(e) => {
              if (e.shiftKey && e.code === "Tab") {
                const target = document.getElementsByName(
                  "officeContact"
                )[0] as HTMLElement;

                if (target) {
                  target.focus();
                }
              }
            }}
          >
            <Typo typoType="btn3m">
              {t("staffManagement:exporter.editDialog.cancelButton")}
            </Typo>
          </Button>

          <Button onClick={() => submitButtonRef.current?.click()}>
            <Typo color="white" typoType="btn3m">
              {t("staffManagement:exporter.editDialog.saveButton")}
            </Typo>
          </Button>
        </DialogFooterContainer>
      }
    >
      {/* 취소 Alert */}
      {isCancelAlertOpen && (
        <CancelAlertDialog
          open={isCancelAlertOpen}
          onOpenChange={setIsCancelAlertOpen}
          onOk={() => onOpenChange(false)}
        />
      )}

      {/* 기업 관리자 1명 이상 Alert Dialog */}
      {isAdminAlertDialogOpen && (
        <EditAlertDialog
          isOpen={isAdminAlertDialogOpen}
          setIsOpen={setIsAdminAlertDialogOpen}
          handleSubmit={() => setIsAdminAlertDialogOpen(false)}
          isAdminSeconds={isAdminSeconds}
        />
      )}

      <Form onSubmit={handleSubmit(handleSubmitClick)}>
        <FormItem
          label={t("staffManagement:exporter.editDialog.name")}
          type="text"
          name="name"
          control={control}
          rules={{ required: true }}
          errorsMessage={{
            required: t("error:required"),
          }}
          direction="vertical"
          inputProps={{
            placeholder: t(
              "staffManagement:exporter.editDialog.namePlaceholder"
            ),
          }}
        />

        <FormItem
          label={t("staffManagement:exporter.editDialog.email")}
          type="text"
          name="email"
          control={control}
          rules={{ required: true }}
          errorsMessage={{
            required: t("error:required"),
          }}
          direction="vertical"
          inputProps={{
            disabled: true,
          }}
        />

        <UserTypeContainer>
          <FormItem
            label={t("staffManagement:exporter.editDialog.authority")}
            type="radioGroup"
            name="exporterUserType"
            rules={{ required: true }}
            control={control}
            options={[
              {
                label: t(
                  "staffManagement:exporter.editDialog.authorityCorporateManager"
                ),
                value: "CORPORATE_MANAGER",
                disabled: !isCorporateManager,
              },
              {
                label: t(
                  "staffManagement:exporter.editDialog.authorityMiddleManager"
                ),
                value: "MIDDLE_MANAGER",
                disabled: !isCorporateManager,
              },
              {
                label: t(
                  "staffManagement:exporter.editDialog.authorityManager"
                ),
                value: "MANAGER",
                disabled: !isCorporateManager,
              },
            ]}
            direction="vertical"
          />
          <Typo color="gray7" typoType="b9r">
            {t("staffManagement:exporter.editDialog.exporterUserTypeNotice")}
          </Typo>
        </UserTypeContainer>

        <FormItem
          label={t("common:staffType")}
          type="radioGroup"
          name="exporterUserMainFieldType"
          rules={{ required: true }}
          control={control}
          options={CLIENT_TYPE_OPTION_LIST.map((item) => {
            return {
              ...item,
              label: t(item.langKey),
              disabled:
                !isBothType || !isCorporateManager || !isCompanySubscription,
            };
          })}
          direction="vertical"
        />

        <UseContainer>
          <FormItem
            label={t("staffManagement:exporter.editDialog.use")}
            type="radioGroup"
            name="isActivated"
            control={control}
            rules={{ required: true }}
            options={[
              { label: "Y", value: "Y" },
              { label: "N", value: "N" },
            ]}
            direction="vertical"
          />

          <UseHistory>
            <li>
              <Typo color="gray6" typoType="b9r">
                {t("staffManagement:exporter.editDialog.chageDate")}
              </Typo>
              <Typo color="gray7" typoType="b9r">
                {`(${
                  selectData?.exporterUserListQueryResultDto
                    .isActivatedChangedAt
                    ? getFormattedTimezoneDateWithOffset({
                        date: (selectData?.exporterUserListQueryResultDto
                          .isActivatedChangedAt || "") as any,
                        format: DATE_FORMAT_STRINGS.YYYY_MM_DD_HH_mm,
                        gmtOffset: gmtOffset || 0,
                      })
                    : "-"
                })`}
              </Typo>
            </li>
            <li>
              <Typo color="gray6" typoType="b9r">
                {t("staffManagement:exporter.editDialog.editor")}
              </Typo>
              <Typo color="gray7" typoType="b9r">
                {`${
                  selectData?.exporterUserListQueryResultDto
                    .isActivatedChangeExporterUser?.name
                    ? selectData?.exporterUserListQueryResultDto
                        .isActivatedChangeExporterUser?.name
                    : `(-)`
                }`}
              </Typo>
            </li>
          </UseHistory>
        </UseContainer>

        <FormItem
          label={t("staffManagement:exporter.editDialog.categoryInCharge")}
          type="multipleSelect"
          name="mainCategory"
          control={control}
          options={mainCategoryFilterList}
          direction="vertical"
          rules={{ required: true }}
          inputProps={{
            getPopupContainer: (triggerNode) => {
              return triggerNode.parentElement;
            },
            onRemoveItem: (value) => {
              const watchCategory = watch("mainCategory");
              const watchCategoryFilter = watchCategory.filter(
                (item) => item !== value
              );
              setValue("mainCategory", watchCategoryFilter);
            },
            placeholder: t(
              "staffManagement:exporter.editDialog.categoryInChargePlaceholder"
            ),
            disableValues:
              // 일반매니저, 중간관리자가 아닌 경우 필터링 없이 그대로 유지
              exporterUserType === "MANAGER" ||
              exporterUserType === "MIDDLE_MANAGER"
                ? mainCategoryStringList.filter(
                    (category) => !userMainCategory.includes(category)
                  )
                : undefined,
          }}
          errorsMessage={{
            required: t("error:required"),
          }}
        />

        <ContactFormItem
          label={t("staffManagement:exporter.editDialog.contactPersonal")}
          prefixValue={watch("personalContactPrefix") || ""}
          restContactValue={watch("personalContact") || ""}
          prefixName="personalContactPrefix"
          restContactName="personalContact"
          register={register}
          setValue={setValue}
          direction="vertical"
          isRequired={false}
        />

        <ContactFormItem
          label={t("staffManagement:exporter.editDialog.contactOffice")}
          prefixValue={watch("officeContactPrefix") || ""}
          restContactValue={watch("officeContact") || ""}
          prefixName="officeContactPrefix"
          restContactName="officeContact"
          register={register}
          setValue={setValue}
          direction="vertical"
          isRequired={false}
        />
        <Hidden
          ref={submitButtonRef}
          type="submit"
          onFocus={() => cancelButtonRef.current?.focus()}
        />
      </Form>
    </Dialog>
  );
}

export default EditDialog;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const UseContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UseHistory = styled.ul`
  display: flex;
  gap: 8px;

  li {
    display: flex;
    align-items: center;
    gap: 4px;

    &::before {
      content: "•";
      color: ${colorSet.gray7};
    }
  }
`;

const Hidden = styled.button`
  position: absolute;
  width: 0;
  height: 0;
  padding: 0;
  overflow: hidden;
  border: 0;
`;

const UserTypeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
